import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import useHttpClient from './useHttpClient';

interface IFieldValues {
  fieldSelector: string;
  fieldName: string;
  value: number | string | boolean;
}
interface IBillingEntryData {
  projectId: number;
  isDraft: boolean;
  userId: string;
  description: string;
  time: string;
  timeCodes: {
    [key: string]: IFieldValues;
  }
}

interface IBillingEntryBody {
  projectId: number;
  isDraft: boolean;
  billingType: string;
  fieldValues: IFieldValues[];
}

interface IBillingEntryResponse {
  id: string;
}

export interface IBillingEntryPayload {
  addBillingEntry: (billingEntryData: IBillingEntryData) => Promise<IBillingEntryResponse>;
  called: boolean;
  loading: boolean;
  error?: Error;
  billingEntryRes?: IBillingEntryResponse;
}

export const formatBillingEntryBody = (data: IBillingEntryData): IBillingEntryBody => ({
  projectId: data.projectId,
  isDraft: data.isDraft,
  billingType: 'Time',
  fieldValues: [
    {
      fieldSelector: 'BillingItemUser',
      fieldName: 'User',
      value: data.userId,
    },
    {
      fieldSelector: 'BillingItemDescription',
      fieldName: 'Description',
      value: data.description,
    },
    {
      fieldSelector: 'BillingItemQuantity',
      fieldName: 'Quantity',
      value: data.time,
    },
    {
      fieldSelector: 'BillingItemDate',
      fieldName: 'Date',
      // Per billing team this should be submitted as the users Local TZ
      value: new Date().toLocaleString(),
    },
    {
      fieldSelector: 'BillingItemIsBillable',
      fieldName: 'Is Billable',
      value: true,
    },
    {
      fieldSelector: 'BillingItemIsChargeable',
      fieldName: 'Is Chargeable',
      value: true,
    },
    ...Object.keys(data.timeCodes).map((tc: string) => data.timeCodes[tc]),
  ],
});

const useMutateBillingEntry = (): IBillingEntryPayload => {
  const httpClient = useHttpClient();
  const [called, setCalled] = useState(false);

  const {
    mutate: addBillingEntry,
    data: billingEntryRes,
    error,
    isPending: loading,
   } = useMutation<IBillingEntryResponse, Error, IBillingEntryData>({
    mutationFn: async (billingEntryData: IBillingEntryData) => {
      const payload = formatBillingEntryBody(billingEntryData);
      const response = await httpClient.put('/billing', payload);
      return response.data;
    },
  });


  const addBillingEntryWithPromise = async (billingEntryData: IBillingEntryData) => {
     setCalled(true);
      return new Promise<IBillingEntryResponse>((resolve, reject) => {
        addBillingEntry(billingEntryData, {
          onSuccess: (data) => resolve(data),
          onError: (err) => reject(err),
        });
      });
    };

  return {
    addBillingEntry: addBillingEntryWithPromise,
    called,
    loading,
    error: error as Error || undefined,
    billingEntryRes: !error && billingEntryRes ? billingEntryRes : undefined,
  };
};

export default useMutateBillingEntry;
